/**
 * 动画偏好 store —— 强度 + 系统级 reduced-motion 检测, localStorage 持久化。
 *
 * 三档强度:
 *   - off:     完全静止 (Pinia / Vue 内部 200ms 默认 transition 仍在, 但人眼几乎察觉不到);
 *              AnimationLayer 不渲染任何东西, Card 立即翻面, Community 全部直出。
 *   - minimal: 入场/出场过渡, Card 翻面动画; 不画 FlyingChip 飞行。
 *   - full:    全开 —— FlyingChip 贝塞尔曲线飞行 + 摊牌金光 + actor pulse。
 *
 * "snapshot 是真相, event 只是 hint" 原则在这里的体现:
 * 任何动画都是装饰, 不参与状态计算 —— 即使 effective='off' 整套系统功能照常。
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type AnimationIntensity = 'off' | 'minimal' | 'full'

const LS_KEY = 'poker_anim_intensity'

function loadFromStorage(): AnimationIntensity | null {
  const v = localStorage.getItem(LS_KEY)
  return (v === 'off' || v === 'minimal' || v === 'full') ? v : null
}

function saveToStorage(v: AnimationIntensity | null): void {
  if (v === null) localStorage.removeItem(LS_KEY)
  else            localStorage.setItem(LS_KEY, v)
}

/** 检测系统 reduced-motion 偏好 (动态, 用户在系统设置切换会反应)。*/
function makeReducedMotionRef() {
  const mql = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')
  const r = ref<boolean>(!!mql?.matches)
  if (mql) {
    const listener = (e: MediaQueryListEvent) => { r.value = e.matches }
    // 现代 Safari/Chrome 都支持 addEventListener('change')
    if ('addEventListener' in mql) mql.addEventListener('change', listener)
  }
  return r
}

export interface ReactEventEntry {
  fromPid:   string
  targetPid: string
  emoji:     string
  id:        number
}

export const useAnimationStore = defineStore('animations', () => {
  /** 用户显式选择; null 表示"跟随系统"。*/
  const userChoice = ref<AnimationIntensity | null>(loadFromStorage())
  const reducedMotion = makeReducedMotionRef()

  /** 实际生效强度: 用户显式 > 系统 reduced-motion (映射到 minimal) > 默认 full。*/
  const effective = computed<AnimationIntensity>(() => {
    if (userChoice.value !== null) return userChoice.value
    return reducedMotion.value ? 'minimal' : 'full'
  })

  /** 各档下是否允许某类动画 (供组件直接读)。*/
  const enableChipFly = computed(() => effective.value === 'full')
  const enableCardFlip = computed(() => effective.value !== 'off')
  const enableSubtlePulse = computed(() => effective.value !== 'off')
  const enableTransitions = computed(() => effective.value !== 'off')

  function setIntensity(v: AnimationIntensity | null): void {
    userChoice.value = v
    saveToStorage(v)
  }

  const _reactCounter = ref(0)
  const _reactListeners = new Set<(e: ReactEventEntry) => void>()

  function onReact(cb: (e: ReactEventEntry) => void): () => void {
    _reactListeners.add(cb)
    return () => _reactListeners.delete(cb)
  }

  function emitReact(fromPid: string, targetPid: string, emoji: string): void {
    const id = ++_reactCounter.value
    const entry: ReactEventEntry = { fromPid, targetPid, emoji, id }
    for (const cb of _reactListeners) cb(entry)
  }

  return {
    userChoice, reducedMotion, effective,
    enableChipFly, enableCardFlip, enableSubtlePulse, enableTransitions,
    setIntensity,
    onReact, emitReact,
  }
})
